import React, { useEffect, useMemo, useState } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../../ui/dialog";
import { toast } from "sonner";
import { Loader2, RefreshCw, Send } from "lucide-react";

const STATUS_LABEL = {
  sent: { label: "Verstuurd", cls: "bg-green-100 text-green-800" },
  skipped_no_key: { label: "Geen Resend key", cls: "bg-amber-100 text-amber-800" },
  skipped_no_recipient: { label: "Geen ontvanger", cls: "bg-gray-100 text-gray-700" },
  failed: { label: "Mislukt", cls: "bg-red-100 text-red-800" },
};

function StatusPill({ status }) {
  if (!status) return <span className="text-xs text-gray-400">—</span>;
  const cfg = STATUS_LABEL[status] || { label: status, cls: "bg-gray-100 text-gray-700" };
  return (
    <span className={`text-[10px] uppercase tracking-wider font-chivo font-bold px-2 py-0.5 ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

export default function DocumentCopyLogsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [settings, setSettings] = useState(null);

  const typeOptions = useMemo(
    () => settings?.document_type_labels || {},
    [settings],
  );

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      if (typeFilter) params.set("document_type", typeFilter);
      if (statusFilter) params.set("status", statusFilter);
      const r = await adminApi.get(`/admin/document-copy-logs?${params.toString()}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [typeFilter, statusFilter]);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.get("/admin/document-copy-settings");
        setSettings(r.data);
      } catch (e) { /* manager kan settings ook lezen, edge-case medewerker → 403 */ }
    })();
  }, []);

  return (
    <div className="space-y-4" data-testid="docs-copy-logs-tab">
      {settings && (
        <div className="bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-brand-900" data-testid="docs-copy-settings">
          <div className="flex flex-wrap items-center gap-3">
            <Send className="w-4 h-4 text-brand-500" />
            <span>Interne kopieën gaan naar:</span>
            <span className="font-mono font-bold" data-testid="docs-copy-email">{settings.copy_to}</span>
            <span className="text-xs text-gray-600 ml-auto">
              Wijzig dit via Instellingen → Productieconfiguratie ({"DOCUMENT_COPY_EMAIL"})
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Input
          data-testid="docs-copy-search"
          placeholder="Zoek op nummer/klant/e-mail..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && refresh()}
          className="rounded-none max-w-xs"
        />
        <select
          data-testid="docs-copy-type-filter"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-none border border-gray-300 bg-white px-3 py-2 text-sm h-10"
        >
          <option value="">Alle documenttypes</option>
          {Object.entries(typeOptions).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          data-testid="docs-copy-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-none border border-gray-300 bg-white px-3 py-2 text-sm h-10"
        >
          <option value="">Alle statussen</option>
          <option value="sent">Verstuurd</option>
          <option value="skipped_no_key">Geen Resend key</option>
          <option value="failed">Mislukt</option>
        </select>
        <Button onClick={refresh} variant="outline" className="rounded-none" data-testid="docs-copy-refresh">
          <RefreshCw className="w-4 h-4 mr-2" />Verversen
        </Button>
        <div className="ml-auto text-xs text-gray-500">{items.length} logs</div>
      </div>

      <div className="bg-white border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider font-chivo font-bold text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Nummer</th>
              <th className="px-3 py-2 text-left">Klant</th>
              <th className="px-3 py-2 text-left">Bron</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Datum</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={7} className="p-6 text-center text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Laden...
              </td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="p-6 text-center text-gray-500">Nog geen documentkopieën gelogd.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} data-testid={`docs-copy-row-${it.id}`} className="hover:bg-gray-50">
                <td className="px-3 py-2 text-xs">
                  {typeOptions[it.document_type] || it.document_type}
                </td>
                <td className="px-3 py-2 font-mono text-[12px]">{it.document_number || "—"}</td>
                <td className="px-3 py-2">
                  <div>{it.customer_name || "—"}</div>
                  <div className="text-xs text-gray-500">{it.customer_email || ""}</div>
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">{it.source || "—"}</td>
                <td className="px-3 py-2"><StatusPill status={it.status} /></td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {it.created_at && new Date(it.created_at).toLocaleString("nl-NL")}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-none"
                    onClick={() => setDetail(it)}
                    data-testid={`docs-copy-open-${it.id}`}
                  >
                    Open
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-xl" data-testid="docs-copy-detail">
          <DialogHeader>
            <DialogTitle>Documentkopie</DialogTitle>
            <DialogDescription>
              {detail?.created_at && new Date(detail.created_at).toLocaleString("nl-NL")}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <Kv label="Documenttype" value={typeOptions[detail.document_type] || detail.document_type} />
              <Kv label="Nummer" value={detail.document_number || "—"} />
              <Kv label="Document ID" value={detail.document_id || "—"} />
              <Kv label="Bestandsnaam" value={detail.filename || "—"} />
              <Kv label="Klantnaam" value={detail.customer_name || "—"} />
              <Kv label="Klant e-mail" value={detail.customer_email || "—"} />
              <Kv label="Bestemming" value={detail.destination || "—"} />
              {detail.total != null && (
                <Kv label="Totaal" value={`${detail.currency || "EUR"} ${Number(detail.total).toFixed(2)}`} />
              )}
              {detail.language && <Kv label="Taal" value={detail.language} />}
              <Kv label="Bron" value={detail.source || "—"} />
              <Kv label="Aangemaakt door" value={detail.triggered_by_email || "publieke flow"} />
              <Kv label="Kopie verstuurd naar" value={detail.email_to} />
              <div className="flex items-center gap-2">
                <Label className="text-[10px] uppercase">Status</Label>
                <StatusPill status={detail.status} />
              </div>
              {detail.error_message_safe && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 mt-2" data-testid="docs-copy-error">
                  <b>Fout:</b> {detail.error_message_safe}
                </div>
              )}
              <p className="text-[11px] text-gray-500 mt-3">
                In het log wordt nooit de PDF-inhoud of de e-mailbody bewaard — alleen veilige metadata.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDetail(null)}>Sluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kv({ label, value }) {
  return (
    <div className="flex gap-2">
      <div className="text-gray-500 w-44 text-[11px] uppercase tracking-wider">{label}</div>
      <div className="text-brand-900 break-all">{value}</div>
    </div>
  );
}
